import React, { useEffect, useState } from 'react';
import { observer } from "mobx-react-lite";
import { useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import useRestricted from "../hooks/useRestricted";
import { AppContext } from "../contexts/AppContext";
import { RaceItem } from "../components/races/RaceItem";
import InfiniteScroll from "react-infinite-scroll-component";
import Logo from "../components/common/Logo";
import useTheme from "@mui/material/styles/useTheme";
import useMediaQuery from "@mui/material/useMediaQuery";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import posthog from "posthog-js";
import Button from '@mui/material/Button';
import AddOutlined from "@mui/icons-material/AddOutlined";
import Dialog from '@mui/material/Dialog';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import IconButton from '@mui/material/IconButton';
import ClearIcon from '@mui/icons-material/Clear';
import { Race } from '../services/RacesService';

const Races: React.FC = observer(() => {
    useRestricted();

    const intl = useIntl();
    const navigate = useNavigate();
    const theme = useTheme();
    const isUpMD = useMediaQuery(theme.breakpoints.up('md'));

    const { userService, racesService } = React.useContext(AppContext);

    const [loadingStarted, setLoadingStarted] = useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const [addRaceDialogOpen, setAddRaceDialogOpen] = React.useState(false);
    const [raceAdded, setRaceAdded] = React.useState(false);
    const [searchQuery, setSearchQuery] = React.useState('');
    const [searchResults, setSearchResults] = React.useState<Race[]>([]);
    const [isSearching, setIsSearching] = React.useState(false);

    useEffect(() => {
        if (!userService.user?.id) {
            return;
        }

        if (!loadingStarted) {
            racesService.loadRaces(true);
            setLoadingStarted(true);
        }
    }, [userService.user?.id, racesService, loadingStarted]);

    useEffect(() => {
        const searchRaces = async () => {
            if (searchQuery.trim().length < 3) {
                setIsSearching(false);
                return;
            }

            setIsSearching(true);
            try {
                const results = await racesService.searchRacesByName(searchQuery);
                setSearchResults(results);
            } catch (error) {
                console.error('Error searching races:', error);
                setSearchResults([]);
            } finally {
                setIsSearching(false);
            }
        };

        const debounceTimer = setTimeout(() => {
            searchRaces();
        }, 300);

        return () => clearTimeout(debounceTimer);
    }, [racesService, searchQuery]);

    const handleAddRace = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const data = new FormData(event.currentTarget);
        if (!data.get('raceName') || !data.get('raceWebsite') || !data.get('raceDistance')) {
            setError("error.allFieldsRequired");
            return;
        }

        try {
            await racesService.addRaceRequest({
                raceName: data.get('raceName') as string,
                raceWebsite: data.get('raceWebsite') as string,
                raceDistance: data.get('raceDistance') as string,
                userId: userService.user?.id || "",
                userEmail: userService.user?.email || "",
                createdAt: new Date()
            });
            setAddRaceDialogOpen(false);
            setError(null);
            setRaceAdded(true);
        } catch (error) {
            setError("error.raceAddition");
        }
    };

    const handleClickRace = (id: string) => {
        navigate(`/plans/new?race=${id}`);
        posthog.capture('race_clicked', { raceId: id });
    };

    const handleClearSearch = () => {
        setSearchQuery('');
        setSearchResults([]);
    };

    const loadMoreRaces = () => {
        if (!searchQuery) {
            racesService.loadRaces();
        }
    };

    const displayRaces = searchQuery.trim().length >= 3 ? searchResults : racesService.races;
    const hasMore = searchQuery.trim().length >= 3 ? false : racesService.hasMoreRaces;

    if (racesService.isLoadingRaces && !loadingStarted) {
        return (
            <Box height="100vh" width="100%" display="flex" alignItems="center" justifyContent="center">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <>
            <Box paddingX={4} paddingTop={4} display="flex" alignItems="center" justifyContent="space-between" position="relative">
                <Typography variant="h4">{intl.formatMessage({ id: "nav.races" })}</Typography>
                {isUpMD && (
                    <Box position="absolute" left="50%" sx={{ transform: 'translateX(-50%)' }}>
                        <Logo width={100} />
                    </Box>
                )}
                <Button variant="contained" color="primary" onClick={() => setAddRaceDialogOpen(true)} startIcon={<AddOutlined />}>
                    {intl.formatMessage({ id: "races.newRace" })}
                </Button>
            </Box>

            <Box paddingX={4} marginTop={4}>
                <TextField
                    fullWidth
                    variant="outlined"
                    placeholder={intl.formatMessage({ id: "races.searchPlaceholder" })}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                        endAdornment: searchQuery && (
                            <InputAdornment position="end">
                                <IconButton onClick={handleClearSearch} edge="end" size="small">
                                    <ClearIcon />
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />
                <Typography variant="body2" sx={{ mt: 2 }} textAlign="center">
                    <strong>{intl.formatMessage({ id: "races.explanation" })}</strong>
                </Typography>
            </Box>

            <Box marginTop={2}>
                {!!userService.user?.id && (
                    <InfiniteScroll
                        dataLength={displayRaces.length}
                        next={loadMoreRaces}
                        hasMore={hasMore}
                        scrollThreshold={1}
                        loader={
                            <Box height={100} width="100%" display="flex" alignItems="center" justifyContent="center">
                                <CircularProgress />
                            </Box>
                        }
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            flexWrap: 'wrap',
                            marginTop: "-32px",
                            width: "calc(100% + 32px)",
                            marginLeft: "-32px",
                            padding: "32px",
                        }}
                    >
                        {isSearching ? (
                            <Box width="100%" display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                                <CircularProgress />
                            </Box>
                        ) : (displayRaces.length > 0) ? (
                            displayRaces.map((race) => (
                                <RaceItem
                                    key={race.id}
                                    race={race}
                                    onClick={() => handleClickRace(race.id)}
                                />
                            ))
                        ) : (searchQuery.trim().length >= 3) ? (
                            <Box width="100%" display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                                <Typography variant="body1" color="text.secondary">
                                    {intl.formatMessage({ id: "races.noResults" })}
                                </Typography>
                            </Box>
                        ) : null}
                    </InfiniteScroll>
                )}
            </Box>
            <Dialog open={addRaceDialogOpen} onClose={() => setAddRaceDialogOpen(false)}>
                <DialogTitle>{intl.formatMessage({ id: "races.newRace" })}</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mt: 2 }}>
                        <strong>{intl.formatMessage({ id: "plans.newPlanAddRace1" })}</strong>
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        <strong>{intl.formatMessage({ id: "plans.newPlanAddRace2" })}</strong>
                    </Typography>
                    <Box component="form" onSubmit={handleAddRace} noValidate id="add-race-form">
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            id="raceName"
                            label={intl.formatMessage({ id: "form.raceName" })}
                            name="raceName"
                            onChange={() => setError(null)}
                        />
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            id="raceWebsite"
                            label={intl.formatMessage({ id: "form.raceWebsite" })}
                            name="raceWebsite"
                            onChange={() => setError(null)}
                        />
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            id="raceDistance"
                            label={intl.formatMessage({ id: "form.raceDistance" })}
                            name="raceDistance"
                            onChange={() => setError(null)}
                        />

                        {error && (
                            <Alert severity="error">
                                {intl.formatMessage({ id: error, defaultMessage: intl.formatMessage({ id: "error.unknown" }) })}
                            </Alert>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddRaceDialogOpen(false)} color="primary">
                        {intl.formatMessage({ id: "action.cancel" })}
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        form="add-race-form"
                    >
                        {intl.formatMessage({ id: "plans.addRace" })}
                    </Button>
                </DialogActions>
            </Dialog>
            <Snackbar
                open={raceAdded}
                message={intl.formatMessage({ id: "plans.raceRequestConfirmation1" })}
                autoHideDuration={10000}
                onClose={() => setRaceAdded(false)}
                ContentProps={{
                    sx: {
                        background: theme.palette.success.main,
                    }
                }}
            />
        </>
    );
});

export default Races;